import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { AtendenteService } from '../services/atendente.service';
import { IAtendente, IFiltroListagemAtendente, IFiltroListagemTodosAtendente } from '../data/atendente';
import { ICompany } from '../../companies/models/company.interface';
import { CompanyService } from '../../companies/services/company.service';
import { AttendantStatusService } from '../../user-support/services/attendant-status.service';

const SITUACAO_TODOS = '';
const SITUACAO_ATIVOS = 'true';
const SITUACAO_INATIVOS = 'false';

@Component({
  selector: 'app-atendentes',
  templateUrl: './atendentes.component.html',
  styleUrls: ['./atendentes.component.scss']
})
export class AtendentesComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  atendentes: IAtendente[] = [];
  listagemCarregada = false;
  ordenarControl = new UntypedFormControl(1);
  nameControl = new UntypedFormControl('');
  companyControl = new UntypedFormControl('');
  situacaoControl = new UntypedFormControl(SITUACAO_TODOS);
  companies: ICompany[] = [];

  opcoesOrdenacao = [
    {
      value: EOrdenacao.AlfabeticaAZ,
      label: 'Alfabética - de A a Z'
    },
    {
      value: EOrdenacao.AlfabeticaZA,
      label: 'Alfabética - de Z a A'
    }
  ];

  opcoesSituacao = [
    { value: SITUACAO_TODOS, label: 'Todos' },
    { value: SITUACAO_ATIVOS, label: 'Ativos' },
    { value: SITUACAO_INATIVOS, label: 'Inativos' }
  ];

  dadosPaginacao = {
    ordenacao: EOrdenacao.AlfabeticaAZ,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };

  companyId?: string;
  partnerId?: string;
  accountRole?: string;

  private readonly destruido$ = new Subject<void>();
  private readonly recarregar$ = new Subject<void>();

  constructor(
    private readonly atendentesService: AtendenteService,
    private readonly sessionService: SessionService,
    private readonly attendantStatusService: AttendantStatusService,
    private readonly companyService: CompanyService
  ) { }

  ngOnInit(): void {
    this.companyId = this.sessionService.getSession().companyId;
    this.partnerId = this.sessionService.getSession().partnerId;
    this.accountRole = this.sessionService.getSession().accountRole;

    if (!this.companyId) {
      this.companyService
        .listarEmpresasAtivas()
        .pipe(takeUntil(this.destruido$))
        .subscribe((res) => {
          this.companies = res.data ?? [];
        });
    }

    this.recarregar$
      .pipe(
        switchMap(() => this.buscarAtendentes()),
        takeUntil(this.destruido$)
      )
      .subscribe((atendentes) => {
        this.atendentes = atendentes;
        this.listagemCarregada = true;
      });

    this.carregarAtendentes();

    this.ordenarControl.valueChanges.pipe(takeUntil(this.destruido$)).subscribe((ordenacaoSelecionada: EOrdenacao) => {
      this.dadosPaginacao.ordenacao = ordenacaoSelecionada;
      this.recarregarPrimeiraPagina();
    });

    this.nameControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destruido$))
      .subscribe(() => this.recarregarPrimeiraPagina());

    this.companyControl.valueChanges
      .pipe(takeUntil(this.destruido$))
      .subscribe(() => this.recarregarPrimeiraPagina());

    this.situacaoControl.valueChanges
      .pipe(takeUntil(this.destruido$))
      .subscribe(() => this.recarregarPrimeiraPagina());
  }

  ngOnDestroy(): void {
    this.destruido$.next();
    this.destruido$.complete();
  }

  carregarAtendentes(): void {
    this.recarregar$.next();
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;

    this.carregarAtendentes();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;

    this.carregarAtendentes();
  }

  public downloadExcel(): void {
    const filtros: IFiltroListagemAtendente = {
      orderBy: 0,
      pageNumber: 0,
      pageSize: 0
    };

    if (this.companyId) {
      filtros.companyId = this.companyId;
    }

    if (this.partnerId) {
      filtros.partnerId = this.partnerId;
    }

    if (this.accountRole) {
      filtros.accountRole = parseInt(this.accountRole);
    }

    this.atendentesService.exportarExcel(filtros)

  }

  private recarregarPrimeiraPagina(): void {
    this.dadosPaginacao.paginaSelecionada = 0;
    this.carregarAtendentes();
  }

  private montarFiltros(): IFiltroListagemTodosAtendente {
    const filtros: IFiltroListagemTodosAtendente = {
      orderBy: Number(this.dadosPaginacao.ordenacao),
      pageNumber: Number(this.dadosPaginacao.paginaSelecionada),
      pageSize: Number(this.dadosPaginacao.tamanhoPagina)
    };

    const companyIdEfetivo = this.companyId || this.companyControl.value || undefined;
    if (companyIdEfetivo) {
      filtros.companyId = companyIdEfetivo;
    }

    if (this.partnerId) {
      filtros.partnerId = this.partnerId;
    }

    if (this.accountRole) {
      filtros.accountRole = parseInt(this.accountRole);
    }

    const searchText = this.nameControl.value?.trim();
    if (searchText) {
      filtros.searchText = searchText;
    }

    if (this.situacaoControl.value !== SITUACAO_TODOS) {
      filtros.active = this.situacaoControl.value === SITUACAO_ATIVOS;
    }

    return filtros;
  }

  private buscarAtendentes(): Observable<IAtendente[]> {
    return this.atendentesService.listarTodosAtendentes(this.montarFiltros()).pipe(
      tap((res) => {
        this.dadosPaginacao.totalPaginas = Math.ceil(res.count! / this.dadosPaginacao.tamanhoPagina);
        this.dadosPaginacao.itensTotal = res.count!;
      }),
      switchMap((res) => this.preencherDisponibilidade(res.data ?? [])),
      catchError(() => {
        this.dadosPaginacao.totalPaginas = 0;
        this.dadosPaginacao.itensTotal = 0;
        return of([] as IAtendente[]);
      })
    );
  }

  private preencherDisponibilidade(atendentes: IAtendente[]): Observable<IAtendente[]> {
    const ids = atendentes.map((a) => a.id).filter(Boolean);

    if (ids.length === 0) {
      return of(atendentes);
    }

    return this.attendantStatusService.getStatusByIds(ids).pipe(
      map((statusRes) => {
        const statusMap = new Map((statusRes.data ?? []).map((s) => [s.attendantId, s.status]));
        return atendentes.map((a) => ({
          ...a,
          availabilityStatus: statusMap.get(a.id) ?? null
        }));
      }),
      catchError(() => of(atendentes.map((a) => ({ ...a, availabilityStatus: null }))))
    );
  }
}
